/**
 * SAVE-TOAST
 * Floating status indicator for save operations
 */

import { RootState, useAppSelector } from "@/core/root-reducer"
import { STATUS_SAVE_ERROR, STATUS_SAVED, STATUS_SAVING } from "@/redux/_status.types"
import { faCheck, faCircleNotch, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"

interface Props {
  selector: (state: RootState) => string[]
}

type ToastState = "saving" | "saved" | "error"

const MIN_SAVING_DURATION = 250

const ICON_COLORS: Record<ToastState, string> = {
  saving: "#4a4a4a",
  saved: "#22c55e",
  error: "#ef4444"
}

const HIDE_DELAY: Record<"saved" | "error", number> = {
  saved: 1500,
  error: 2500
}

export default function SaveToast({ selector }: Props) {
  const statuses = useAppSelector(selector)
  const [toast, setToast] = useState<ToastState | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savingStartRef = useRef<number | null>(null)

  const aggregated: ToastState | null = statuses.some((s) => s === STATUS_SAVING)
    ? "saving"
    : statuses.some((s) => s === STATUS_SAVE_ERROR)
    ? "error"
    : statuses.some((s) => s === STATUS_SAVED)
    ? "saved"
    : null

  useEffect(() => {
    if (aggregated === "saving") {
      if (timerRef.current) clearTimeout(timerRef.current)
      savingStartRef.current = Date.now()
      setToast("saving")
    } else if ((aggregated === "saved" || aggregated === "error") && toast === "saving") {
      const elapsed = savingStartRef.current ? Date.now() - savingStartRef.current : MIN_SAVING_DURATION
      const remaining = Math.max(0, MIN_SAVING_DURATION - elapsed)
      const next = aggregated

      timerRef.current = setTimeout(() => {
        setToast(next)
        timerRef.current = setTimeout(() => setToast(null), HIDE_DELAY[next])
      }, remaining)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [aggregated])

  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center bg-white shadow-lg">
          <FontAwesomeIcon
            icon={toast === "saving" ? faCircleNotch : toast === "saved" ? faCheck : faTimes}
            spin={toast === "saving"}
            style={{ color: ICON_COLORS[toast] }}
            className="text-lg"
          />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
